"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.QueryProvider = exports.useQueryContext = exports.QueryContext = exports.isPaginatedList = void 0;
const react_1 = __importStar(require("react"));
const react_router_dom_1 = require("react-router-dom");
const useGetQueryGroups_1 = require("./hooks/useGetQueryGroups");
const useRunQueryById_1 = require("./hooks/useRunQueryById");
const plugins_1 = require("./plugins");
function isPaginatedList(value) {
    return (value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Array.isArray(value.data) &&
        typeof value.total === 'number');
}
exports.isPaginatedList = isPaginatedList;
exports.QueryContext = (0, react_1.createContext)(null);
const useQueryContext = (params) => {
    const context = (0, react_1.useContext)(exports.QueryContext);
    if (!context && !(params === null || params === void 0 ? void 0 : params.ignoreUndefined)) {
        throw new Error('useQueryContext must be used within the context of the QueryProvider component');
    }
    return context;
};
exports.useQueryContext = useQueryContext;
const QueryProvider = ({ children, queryBuilderPlugins }) => {
    const location = (0, react_router_dom_1.useLocation)();
    const parentContext = (0, exports.useQueryContext)({ ignoreUndefined: true });
    const parentContextRef = (0, react_1.useRef)(parentContext);
    parentContextRef.current = parentContext;
    const getQueryGroupApi = (0, useGetQueryGroups_1.useGetQueryGroups)();
    const { queryGroups, getQueryGroups } = getQueryGroupApi;
    // upon user login in QMS with a redirect to a custom page,
    // query values do not show up since queryGroups failed to
    // load before the login (forbidden 403).
    // force fetch queryGroups if not yet fetched
    const key = !parentContext && !queryGroups ? location.key : undefined;
    // Only root QueryProvider to get query groups data
    (0, react_1.useEffect)(() => void (!parentContext && getQueryGroups()), [key, parentContext, getQueryGroups]);
    // get array of queries for easier lookup
    const queries = (0, react_1.useMemo)(() => {
        // since queries are fetched at the root QueryContext use
        // parentContext.queries (passing queries down from root)
        if (parentContext === null || parentContext === void 0 ? void 0 : parentContext.queries)
            return parentContext === null || parentContext === void 0 ? void 0 : parentContext.queries;
        if (!queryGroups) {
            return [];
        }
        return queryGroups.reduce((results, queryGroup) => {
            results.push(...queryGroup.queries);
            return results;
        }, []);
    }, [queryGroups, parentContext === null || parentContext === void 0 ? void 0 : parentContext.queries]);
    const [queryResults, setQueryResults] = (0, react_1.useState)([]);
    const queryResultsRef = (0, react_1.useRef)(queryResults);
    const runQueryByIdApi = (0, useRunQueryById_1.useRunQueryById)();
    const { queryId, queryData } = runQueryByIdApi;
    const depth = parentContext ? parentContext.depth + 1 : 0;
    const applyQueryData = (0, react_1.useCallback)((queryData, queryId) => {
        var _a;
        const results = [...queryResultsRef.current];
        const result = results.find(result => result.queryId === queryId);
        if (result) {
            result.data = queryData;
        }
        else {
            results.push({ queryId, data: queryData });
        }
        setQueryResults([...results]);
        (_a = parentContextRef.current) === null || _a === void 0 ? void 0 : _a.addQueryResults(results);
    }, []);
    // handle paginated and non-array data
    const getRows = (0, react_1.useCallback)((queryResult) => {
        const { data } = queryResult;
        return isPaginatedList(data)
            ? data.data
            : Array.isArray(data)
                ? data
                : data
                    ? [data]
                    : [];
    }, []);
    (0, react_1.useEffect)(() => {
        queryResultsRef.current = queryResults;
    }, [queryResults]);
    (0, react_1.useEffect)(() => {
        queryData && queryId && applyQueryData(queryData, queryId);
    }, [queryId, queryData, applyQueryData]);
    // expose method for adding query results to a query context
    const addQueryResults = (0, react_1.useCallback)((queryResults) => {
        setQueryResults(results => {
            for (const queryResult of queryResults) {
                // TODO: check params as well
                if (!results.some(r => r.queryId === queryResult.queryId)) {
                    results.push(queryResult);
                }
            }
            return [...results];
        });
    }, []);
    // include parent query results
    (0, react_1.useEffect)(() => {
        if (parentContext && parentContext.queryResults.length > 0) {
            addQueryResults(parentContext.queryResults);
        }
    }, [addQueryResults, parentContext, parentContext === null || parentContext === void 0 ? void 0 : parentContext.queryResults]);
    const iterateQuery = (0, react_1.useCallback)((queryResult, callback) => {
        return getRows(queryResult).map((row, index) => {
            return callback(row, index);
        });
    }, [getRows]);
    return (react_1.default.createElement(exports.QueryContext.Provider, { value: Object.assign(Object.assign(Object.assign({}, getQueryGroupApi), runQueryByIdApi), { queries,
            depth,
            queryResults,
            addQueryResults,
            getRows,
            iterateQuery, queryBuilderPlugins: queryBuilderPlugins || plugins_1.defaultQueryBuilderPlugins }) }, children));
};
exports.QueryProvider = QueryProvider;
//# sourceMappingURL=QueryContext.js.map